import { IconLoader2, IconUpload } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { getClientPaymentUploadTypes, updateClientPaymentUploadTypes } from '@/api/clients';
import { DetailSectionHeader } from '@/components/detail-page-primitives';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';

type PaymentUploadType = 'ui_batch' | 'sftp';

const PAYMENT_UPLOAD_CHANNEL_OPTIONS: Array<{
  value: PaymentUploadType;
  label: string;
  description: string;
}> = [
  {
    value: 'ui_batch',
    label: 'UI Upload',
    description: 'Upload payment files via the web browser',
  },
  {
    value: 'sftp',
    label: 'SFTP',
    description: 'Upload payment files via SFTP',
  },
];

interface ClientPaymentUploadSettingsProps {
  clientId: string;
  clientName: string;
}

export function ClientPaymentUploadSettings({
  clientId,
  clientName,
}: ClientPaymentUploadSettingsProps) {
  const [allowedTypes, setAllowedTypes] = useState<PaymentUploadType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [savingType, setSavingType] = useState<PaymentUploadType | null>(null);

  useEffect(() => {
    setIsLoading(true);
    getClientPaymentUploadTypes(clientId)
      .then((data) => setAllowedTypes(data.allowedUploadTypes))
      .catch(() => toast.error('Failed to load payment upload channel settings'))
      .finally(() => setIsLoading(false));
  }, [clientId]);

  const handleToggle = async (value: PaymentUploadType, enabled: boolean) => {
    const next = enabled
      ? ([...new Set([...allowedTypes, value])] as PaymentUploadType[])
      : allowedTypes.filter((t) => t !== value);

    const previous = allowedTypes;
    setAllowedTypes(next);
    setSavingType(value);

    try {
      const updated = await updateClientPaymentUploadTypes(clientId, next);
      setAllowedTypes(updated.allowedUploadTypes);
    } catch {
      setAllowedTypes(previous);
      toast.error('Failed to update payment upload channel settings');
    } finally {
      setSavingType(null);
    }
  };

  return (
    <Card className="py-0">
      <DetailSectionHeader icon={IconUpload} title="Payment File Upload Channels" />
      <CardContent className="space-y-0 p-4 pt-3">
        <p className="pb-2 text-sm text-muted-foreground">
          Controls which upload channels are available for {clientName}.
        </p>
        {isLoading ? (
          <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
            <IconLoader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        ) : (
          PAYMENT_UPLOAD_CHANNEL_OPTIONS.map(({ value, label, description }, index) => {
            const isEnabled = allowedTypes.includes(value);
            const isSaving = savingType === value;
            const toggleId = `upload-channel-${value}`;

            return (
              <div
                key={value}
                className={`flex items-center justify-between py-3 ${
                  index < PAYMENT_UPLOAD_CHANNEL_OPTIONS.length - 1 ? 'border-b border-border' : ''
                }`}
              >
                <div className="space-y-0.5 flex-1">
                  <Label htmlFor={toggleId} className="text-sm font-medium cursor-pointer">
                    {label}
                  </Label>
                  <p className="text-xs text-muted-foreground">{description}</p>
                </div>
                <div className="flex items-center ml-4">
                  {isSaving ? (
                    <IconLoader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  ) : (
                    <label
                      htmlFor={toggleId}
                      className="relative inline-flex items-center cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        id={toggleId}
                        checked={isEnabled}
                        disabled={isSaving}
                        onChange={(e) => handleToggle(value, e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-input peer-focus-visible:ring-[3px] peer-focus-visible:ring-ring/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-background after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 relative" />
                    </label>
                  )}
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

export default ClientPaymentUploadSettings;
